import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaLock, FaShieldAlt, FaMapMarkerAlt, FaCreditCard } from 'react-icons/fa';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { getCart, clearCart } from '../utils/cartUtils';
import { products, whatsappNumber } from '../data/productsData';
import { calculateShippingFromAddress, extractPincodeFromAddress } from '../utils/shippingUtils';
import { usePincodeValidation } from '../hooks/usePincodeValidation';
import { useToast } from '../components/ToastProvider';
import './CheckoutPage.css';

// API Configuration
const API_BASE = (process.env.REACT_APP_API_BASE || "http://localhost/handmade-haven").replace(/\/$/, "");
const API_ENDPOINT = `${API_BASE}/api/create-razorpay-order.php`; // Updated to Razorpay endpoint
const ADDRESSES_ENDPOINT = `${API_BASE}/api/customer/addresses.php`;

const CheckoutPage = () => {
    const navigate = useNavigate();
    const { customer, csrfToken, refreshOrders, loading: authLoading } = useCustomerAuth();
    const { validateAddress } = usePincodeValidation();
    const { showToast } = useToast();

    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [addresses, setAddresses] = useState([]);
    const [error, setError] = useState("");
    const [pincodeError, setPincodeError] = useState(null);
    const [deliveryMethod, setDeliveryMethod] = useState('delivery'); // 'delivery' or 'pickup'

    // Checkout Form State
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        address: ""
    });

    const [calculatedShipping, setCalculatedShipping] = useState(null);

    // Initialize Page
    useEffect(() => {
        // Wait for auth to finish loading before making decisions
        if (authLoading) {
            return; // Don't do anything while auth is loading
        }

        // 1. Check Auth - ONLY after loading is complete
        if (!customer) {
            navigate('/login', { state: { from: '/checkout' }, replace: true });
            return;
        }

        // 2. Load Cart
        const items = getCart();
        if (items.length === 0) {
            navigate('/cart', { replace: true });
            return;
        }
        setCartItems(items);

        // 3. Pre-fill Customer Data
        setForm(prev => ({
            ...prev,
            name: customer.name || "",
            email: customer.email || "",
            phone: customer.phone || ""
        }));

        // 4. Fetch Saved Addresses
        fetchAddresses();

        setPageLoading(false);
    }, [customer, navigate, authLoading]);

    // Fetch Addresses
    const fetchAddresses = async () => {
        try {
            const res = await fetch(ADDRESSES_ENDPOINT, { credentials: 'include' });
            const data = await res.json();
            if (data.ok && Array.isArray(data.addresses)) {
                setAddresses(data.addresses);
                // Auto-select default if form address is empty
                const defaultAddr = data.addresses.find(a => a.is_default == 1);
                if (defaultAddr && !form.address) {
                    handleAddressSelect(defaultAddr);
                }
            }
        } catch (err) {
            console.warn("Failed to fetch addresses", err);
        }
    };

    // Calculations
    const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    // Update Shipping when address changes
    useEffect(() => {
        if (form.address) {
            const result = calculateShippingFromAddress(form.address, subtotal);
            if (!result.error) {
                setCalculatedShipping(result.amount);
            }
        }
    }, [form.address, subtotal]);

    // Check if any item has freeShipping flag (for testing purposes)
    const hasFreeShippingItem = cartItems.some(item => item.freeShipping === true);
    const calculatedShippingOrZero = calculatedShipping !== null ? calculatedShipping : 0;
    const shippingCost = deliveryMethod === 'pickup' ? 0 : (hasFreeShippingItem ? 0 : calculatedShippingOrZero);
    const totalAmount = subtotal + shippingCost;

    // Handlers
    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        if (e.target.name === 'address') setPincodeError(null);
    };

    const handleAddressSelect = (addrOrEvent) => {
        let addr;
        if (addrOrEvent.target) {
            const selectedId = addrOrEvent.target.value;
            if (!selectedId) return;
            addr = addresses.find(a => a.id == selectedId);
        } else {
            addr = addrOrEvent;
        }

        if (addr) {
            const formattedAddress = `${addr.address}, ${addr.city}, ${addr.state} - ${addr.pincode}`;
            setForm(prev => ({ ...prev, address: formattedAddress }));
        }
    };

    const verifyPayment = async (razorpayResponse, orderId) => {
        try {
            const res = await fetch(`${API_BASE}/api/verify-payment.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrfToken
                },
                credentials: 'include',
                body: JSON.stringify({
                    razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                    razorpay_order_id: razorpayResponse.razorpay_order_id,
                    razorpay_signature: razorpayResponse.razorpay_signature,
                    order_id: orderId
                })
            });

            const data = await res.json();
            if (data.ok) {
                showToast(`Payment Successful! Order ID: ${orderId}`, 'success');
                clearCart();
                refreshOrders?.();
                navigate(`/order-confirmation?order_id=${orderId}`);
            } else {
                setError(data.message || 'Payment verification failed');
                showToast(data.message || 'Payment verification failed', 'error');
            }
        } catch (error) {
            console.error('Verification Error:', error);
            setError('Payment verification failed. Please contact support.');
            showToast('Payment verification failed. Please contact support.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handlePlaceOrder = async (e) => {
        e.preventDefault();
        setError("");
        setPincodeError(null);

        // 1. Basic Validation
        const isAddressRequired = deliveryMethod !== 'pickup';
        if (!form.name?.trim() || !form.phone?.trim() || !form.email?.trim() || (isAddressRequired && !form.address?.trim())) {
            setError("Please fill in all required fields.");
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        // 2. Pincode Validation
        if (deliveryMethod !== 'pickup') {
            const pincodeResult = validateAddress(form.address);
            if (!pincodeResult.valid) {
                const extractedPincode = pincodeResult.pincode || 'your area';
                const whatsappLink = `https://wa.me/${whatsappNumber}?text=Hi! I'd like to inquire about delivery to pincode ${extractedPincode}`;

                setPincodeError({
                    message: pincodeResult.message || 'Delivery not available to this location.',
                    whatsappLink
                });
                setError(pincodeResult.message || `Delivery not available (Pincode: ${extractedPincode})`);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
        }

        setLoading(true);

        try {
            // Prepare Items Payload
            const itemsPayload = cartItems.map(item => {
                return {
                    product_id: item.product_id || item.id,
                    name: item.name,
                    price: item.price,
                    qty: item.quantity,
                    variant_label: item.variant_label || null,  // Cart stores it directly as variant_label
                    image: item.image,
                    quantity: item.quantity
                };
            });

            const payload = {
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim(),
                address: deliveryMethod === 'pickup' ? 'Self Pickup (Ahmedabad)' : form.address.trim(),
                items: itemsPayload,
                subtotal: subtotal,
                shipping: shippingCost,
                total: totalAmount,
                payment_method: deliveryMethod === 'pickup' ? 'pickup' : 'online'
            };

            // 1. Create Order & Get Razorpay Order ID
            const res = await fetch(API_ENDPOINT, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": csrfToken
                },
                body: JSON.stringify(payload),
                credentials: "include"
            });

            const data = await res.json();

            if (!res.ok || !data.ok) {
                throw new Error(data.message || "Failed to initialize payment");
            }

            if (deliveryMethod === 'pickup') {
                showToast(`Order Placed! Redirecting to WhatsApp...`, 'success');
                clearCart();
                refreshOrders?.();
                window.open(`https://wa.me/${whatsappNumber}?text=Hello! I just placed a Self Pickup order. Order ID: %23${data.order_id}. Total Amount: ₹${totalAmount}`, '_blank');
                navigate(`/order-confirmation?order_id=${data.order_id}`);
                return;
            }

            // 2. Initialize Razorpay Checkout
            if (!window.Razorpay) {
                throw new Error("Razorpay SDK not loaded. Please refresh the page.");
            }

            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY_ID, // Ensure this is in .env
                amount: data.amount,
                currency: data.currency,
                name: 'Handmade Haven',
                description: `Order #${data.order_id}`,
                image: '/Handmade haven.png',
                order_id: data.razorpay_order_id,
                handler: (response) => verifyPayment(response, data.order_id),
                prefill: {
                    name: form.name,
                    email: form.email,
                    contact: form.phone
                },
                theme: {
                    color: '#d14c87'
                },
                modal: {
                    ondismiss: () => {
                        setLoading(false);
                        showToast('Payment cancelled', 'info');
                    }
                }
            };

            const razorpay = new window.Razorpay(options);
            razorpay.on('payment.failed', function (response) {
                console.error("Payment Failed", response.error);
                showToast(response.error.description || "Payment failed", 'error');
                setLoading(false);
            });
            razorpay.open();

        } catch (err) {
            console.error("Checkout Error:", err);
            setError(err.message || "Something went wrong. Please try again.");
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setLoading(false);
        }
    };

    // Show loading while auth is being checked OR page is initializing
    if (authLoading || pageLoading) {
        return <div className="checkout-page loading" style={{ paddingTop: '150px', textAlign: 'center', color: '#666' }}>Loading checkout...</div>;
    }

    return (
        <div className="checkout-page">
            <div className="checkout-container">

                {/* Header (Full Width) */}
                <div className="checkout-header">
                    <Link to="/cart" className="back-link">
                        <FaArrowLeft /> Return to Cart
                    </Link>
                    <h1>Checkout</h1>
                </div>

                <div className="checkout-content">
                    {/* Left Column: Form */}
                    <div className="checkout-form-section">
                        {error && (
                            <div className="validation-error">
                                <div>
                                    <strong>Error:</strong> {error}
                                    {pincodeError?.whatsappLink && (
                                        <div style={{ marginTop: '8px' }}>
                                            <a href={pincodeError.whatsappLink} target="_blank" rel="noreferrer" className="whatsapp-link">
                                                Contact us on WhatsApp for assistance
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <form id="checkout-form" onSubmit={handlePlaceOrder}>
                            {/* Contact Info */}
                            <section className="checkout-section">
                                <h2 className="section-title">
                                    <FaShieldAlt style={{ color: '#D4AF37' }} /> Contact Information
                                </h2>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label">Full Name <span style={{ color: 'red' }}>*</span></label>
                                        <input
                                            type="text"
                                            name="name"
                                            className="form-input"
                                            value={form.name}
                                            onChange={handleChange}
                                            required
                                            placeholder="Enter your full name"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Mobile Number <span style={{ color: 'red' }}>*</span></label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            className="form-input"
                                            value={form.phone}
                                            onChange={handleChange}
                                            required
                                            placeholder="+91 98765 43210"
                                        />
                                    </div>
                                    <div className="form-group full-width">
                                        <label className="form-label">Email Address <span style={{ color: 'red' }}>*</span></label>
                                        <input
                                            type="email"
                                            name="email"
                                            className="form-input"
                                            value={form.email}
                                            onChange={handleChange}
                                            required
                                            placeholder="For order confirmation"
                                        />

                                    </div>
                                </div>
                            </section>

                            {/* Delivery Method */}
                            <section className="checkout-section">
                                <h2 className="section-title">
                                    <FaMapMarkerAlt style={{ color: '#D4AF37' }} /> Delivery Method
                                </h2>
                                <div style={{ display: 'flex', gap: '20px', padding: '10px 0' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input 
                                            type="radio" 
                                            name="deliveryMethod" 
                                            value="delivery" 
                                            checked={deliveryMethod === 'delivery'} 
                                            onChange={() => setDeliveryMethod('delivery')} 
                                            style={{ accentColor: '#d14c87', width: '18px', height: '18px' }}
                                        />
                                        <span style={{ fontSize: '1.05rem' }}>Standard Delivery</span>
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input 
                                            type="radio" 
                                            name="deliveryMethod" 
                                            value="pickup" 
                                            checked={deliveryMethod === 'pickup'} 
                                            onChange={() => {
                                                setDeliveryMethod('pickup');
                                                setPincodeError(null);
                                            }}
                                            style={{ accentColor: '#d14c87', width: '18px', height: '18px' }}
                                        />
                                        <span style={{ fontSize: '1.05rem' }}>Self Pickup (Ahmedabad)</span>
                                    </label>
                                </div>
                            </section>

                            {deliveryMethod === 'pickup' && (
                                <section className="checkout-section">
                                    <h2 className="section-title">
                                        <FaMapMarkerAlt style={{ color: '#D4AF37' }} /> Pickup Details
                                    </h2>
                                    <div style={{ padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #ddd' }}>
                                        <p style={{ margin: '0 0 10px 0', fontSize: '15px', color: '#555', lineHeight: '1.5' }}>
                                            <strong>Note:</strong> You have selected Self Pickup. 
                                            After placing the order, you will be redirected to our WhatsApp to coordinate the pickup time and location in Ahmedabad.
                                            There is no shipping charge, and you can pay during pickup.
                                        </p>
                                    </div>
                                </section>
                            )}

                            {/* Shipping Address */}
                            {deliveryMethod === 'delivery' && (
                            <section className="checkout-section">
                                <h2 className="section-title">
                                    <FaMapMarkerAlt style={{ color: '#D4AF37' }} /> Shipping Address
                                </h2>

                                {addresses.length > 0 && (
                                    <div className="form-group">
                                        <label className="form-label">Saved Addresses</label>
                                        <select
                                            className="form-select"
                                            onChange={handleAddressSelect}
                                            defaultValue=""
                                        >
                                            <option value="" disabled>Select a saved address...</option>
                                            {addresses.map(addr => (
                                                <option key={addr.id} value={addr.id}>
                                                    {addr.label} ({addr.city}, {addr.pincode})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <div className="form-group">
                                    <label className="form-label">Complete Address</label>
                                    <textarea
                                        name="address"
                                        className="form-textarea"
                                        value={form.address}
                                        onChange={handleChange}
                                        required
                                        placeholder="House No, Building, Street, Area, City, State - Pincode"
                                    />
                                    <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                                        * Shipping cost is calculated based on pincode.
                                    </small>
                                </div>
                            </section>
                            )}

                            {/* Payment Method - Placeholder for now */}
                            {/* <section className="checkout-section">
                    <h2 className="section-title">
                        <FaCreditCard style={{ color: '#D4AF37' }} /> Payment
                    </h2>
                    <div className="payment-options">
                        <p>Secure online payment will be enabled in the next step.</p>
                    </div>
                </section> */}
                        </form>
                    </div>

                    {/* Right Column: Summary */}
                    <div className="order-summary-section">
                        <div className="checkout-section order-summary-card">
                            <h2 className="section-title">Order Summary</h2>

                            <div className="summary-items">
                                {cartItems.map((item, idx) => (
                                    <div key={idx} className="checkout-item">
                                        <div className="checkout-item-image">
                                            <img src={item.image} alt={item.name} />
                                        </div>
                                        <div className="checkout-item-details">
                                            <div className="checkout-item-name">{item.name}</div>
                                            {item.variant_label && (
                                                <div className="checkout-item-meta">
                                                    Type: {item.variant_label}
                                                </div>
                                            )}
                                            <div className="checkout-item-meta">Qty: {item.quantity}</div>
                                        </div>
                                        <div className="checkout-item-price">
                                            ₹{item.price * item.quantity}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="summary-totals">
                                <div className="total-row">
                                    <span>Subtotal</span>
                                    <span>₹{subtotal}</span>
                                </div>
                                <div className="total-row">
                                    <span>Shipping</span>
                                    <span>{shippingCost === 0 ? <span style={{ color: '#4CAF50' }}>FREE</span> : `₹${shippingCost}`}</span>
                                </div>
                                <div className="total-row final">
                                    <span>Total to Pay</span>
                                    <span>₹{totalAmount}</span>
                                </div>
                            </div>

                            <button
                                type="submit"
                                form="checkout-form"
                                className="btn-pay"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>Processing...</>
                                ) : (
                                    deliveryMethod === 'pickup' ? (
                                        <>Place Order via WhatsApp (₹{totalAmount})</>
                                    ) : (
                                        <><FaLock size={14} /> Pay Now ₹{totalAmount}</>
                                    )
                                )}
                            </button>

                            <div className="guaranteed-safe-checkout">
                                <img
                                    src="/Guaranteed safe checkout.webp"
                                    alt="Guaranteed Safe Checkout"
                                    className="safe-checkout-img"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutPage;
